import { ChangeDetectionStrategy, OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { LegalEntity, CockpitProcess } from '@apps/file-process/file-process.state';
import { ProcessType } from '@apps/file-process/process/process.model';
import { AuthorizeService } from '@platform/auth/authorize.service';
import { ItemsResponseDto } from '@platform/shared/items-response.dto';
import { LoadingService, provideLoadingService } from '@platform/shared/loading.service';
import { FileProcessService } from '@apps/file-process/file-process.service';

@Component({
  selector: 'app-process-widget',
  templateUrl: './process-widget.component.html',
  styleUrls: ['./process-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideLoadingService()],
  standalone: false,
})
export class ProcessWidgetComponent implements OnInit {
  @Input({ required: true }) customerSpaceId!: string;
  @Input() processType!: ProcessType;

  showAll$: Observable<boolean> = of(false);
  public readonly displayRowsCount = 3;
  public cockpitProcessResponse$!: Observable<ItemsResponseDto<CockpitProcess>>;
  public cockpitProcess$!: Observable<CockpitProcess[]>;
  public totalCount$!: Observable<number>;

  legalEntities2$: Observable<LegalEntity[]>;
  createEntityLink$: Observable<string[]>;
  navigateLink$: Observable<string[]>;
  canWriteProcess$: Observable<boolean>;

  constructor(
    public loadingService: LoadingService,
    private store$: Store,
    private authorizeService: AuthorizeService,
    private fileProcessService: FileProcessService,
  ) {}

  ngOnInit(): void {
    this.canWriteProcess$ = this.isAuthorizedToWriteProcess();

    this.legalEntities2$ = this.loadingService
      .showLoadingUntilCompleted(this.fileProcessService.getCustomerSpaceLegalEntitiesForList(this.customerSpaceId, this.processType))
      .pipe(startWith([]), shareReplay(1));

    this.navigateLink$ = combineLatest([this.canWriteProcess$, this.legalEntities2$]).pipe(
      map(([canWriteProcesses, legalEntities]) =>
        this.buildNavigateLink(canWriteProcesses, legalEntities, this.customerSpaceId, this.processType),
      ),
    );

    this.cockpitProcessResponse$ = this.loadingService
      .showLoadingUntilCompleted(this.fileProcessService.getCockpitProcesses(this.customerSpaceId, this.processType))
      .pipe(
        startWith({
          items: [],
          totalCount: 0,
        }),
        shareReplay(1),
      );

    this.cockpitProcess$ = this.cockpitProcessResponse$.pipe(map((e) => e.items));
    this.totalCount$ = this.cockpitProcessResponse$.pipe(map((e) => e.totalCount));
    this.showAll$ = this.totalCount$.pipe(map((total) => total > this.displayRowsCount));
  }

  private isAuthorizedToWriteProcess() {
    const values = new Map<string, string>();
    values.set('customerSpaceId', this.customerSpaceId);
    values.set('processType', this.processType);

    return this.authorizeService.isAuthorized('APP_FILE_PROCESS_WRITE', values);
  }

  private buildNavigateLink(canWriteProcesses: boolean, legalEntities: LegalEntity[], customerSpaceId: string, processType: string) {
    if (!legalEntities) {
      return [];
    }
    if (legalEntities.length === 1 && !canWriteProcesses) {
      return ['/customer-spaces', customerSpaceId, 'file-process', processType, 'legal-entities', legalEntities[0].id, 'processes'];
    }
    return ['/', 'customer-spaces', customerSpaceId, 'file-process', processType, 'legal-entities'];
  }
}
